/**
 * Slack manager-side SenderGate for the opencode runtime (ADR-0047).
 *
 * On Claude Code the slack-channel MCP computes the inbound-access leaves
 * (content filter, sender policy, peer classification) from the raw Slack event
 * and hands them to the ONE shared composer `decideInboundAccess` (ENG-6236).
 * opencode has no in-session channel MCP, so the manager must make the same
 * decision before injecting a turn over HTTP. This module is that composition,
 * reusing the identical core primitives and composer - no replicated policy, so
 * no drift from the live Slack handler.
 *
 * It runs on the RAW Slack event (subtype, bot_id, thread_ts, ...) because the
 * classifiers need those fields, which the normalized InboundMessage discards -
 * so the ingest consumer calls this on the raw event and only normalizes an
 * admitted message. Command detection and the same-org polite-decline copy are
 * intentionally out of scope here (opencode does not dispatch slash commands via
 * this path, and the injection gate needs only admit/deny), so `command` is
 * never supplied and `sameOrg` is false: the composer yields only admit or drop.
 */

import {
  decideSlackMessageForward,
  decideSenderPolicyForward,
  type SlackMessageEvent,
  type SenderPolicy,
} from './slack-inbound-filter.js';
import {
  classifyPeerMessage as classifySlackPeerMessage,
  type SlackPeerClassifierConfig,
} from './slack-peer-classifier.js';
import { classifySlackPolicyBlock } from './sender-policy-decline.js';
import { decideInboundAccess, type InboundAccessInput } from './inbound-access.js';

/**
 * The raw Slack message/app_mention event the gate reads. Extends the content
 * filter's `SlackMessageEvent` with the addressing fields the peer classifier
 * and sender policy consult (the live handler reaches these via casts; we type
 * them explicitly).
 */
export type SlackGateEvent = SlackMessageEvent & {
  channel?: string;
  channel_type?: string;
  thread_ts?: string;
  bot_id?: string;
  parent_user_id?: string;
};

export interface SlackGateDeps {
  senderPolicy: SenderPolicy;
  peerConfig: SlackPeerClassifierConfig;
  /** The agent bot's own Slack user id (auth.test `user_id`). */
  botUserId: string | undefined;
}

/**
 * Structurally the opencode bridge's `GateDecision` (kept local to avoid a
 * governance -> framework import). `admit:false` carries the composer's drop
 * reason for telemetry.
 */
export type SlackGateDecision = { admit: true } | { admit: false; reason: string };

/**
 * Decide whether a raw Slack inbound event should be injected into the agent.
 * Faithful to `decideInboundAccess`'s precedence: content filter -> self ->
 * sender policy -> peer governance -> admit.
 */
export function evaluateSlackInboundGate(
  evt: SlackGateEvent,
  deps: SlackGateDeps,
): SlackGateDecision {
  const isBot = Boolean(evt.bot_id);

  // Leaf 1: content filter (channel noise: bad subtype, no user, empty).
  // ENG-8669 follow-up: the third-party grant has to be threaded in here too.
  // This function exists to be faithful to the live handler, so if only one of
  // the two passes the allowlist they diverge — and a silent divergence
  // between the real path and its mirror is the same class of defect as the
  // one being fixed.
  const content = decideSlackMessageForward(evt, deps.peerConfig.third_party_bots);

  // Leaf 2: sender policy (applies to every sender). Carry the block sub-reason
  // for telemetry, exactly as the live handler does.
  const spDecision = decideSenderPolicyForward(evt, deps.senderPolicy);
  const senderPolicy: InboundAccessInput['senderPolicy'] = spDecision.forward
    ? { forward: true }
    : { forward: false, reason: classifySlackPolicyBlock(evt, deps.senderPolicy) };

  // Leaf 3: peer-agent governance (bot-authored messages only).
  const peer: InboundAccessInput['peer'] = isBot
    ? (() => {
        const c = classifySlackPeerMessage(
          {
            text: evt.text,
            channel: evt.channel ?? '',
            channel_type: evt.channel_type,
            user: evt.user,
            bot_id: evt.bot_id,
            thread_ts: evt.thread_ts,
            parent_user_id: evt.parent_user_id,
          },
          deps.peerConfig,
          { bot_user_id: deps.botUserId ?? null },
        );
        return { kind: c.kind, reason: c.kind === 'drop' ? c.reason : undefined };
      })()
    : undefined;

  const decision = decideInboundAccess({
    content,
    isSelf: Boolean(evt.user && deps.botUserId && evt.user === deps.botUserId),
    isBot,
    peer,
    senderPolicy,
    // Injection gate: no command dispatch and no same-org decline copy needed.
    sameOrg: false,
  });

  // 'command' cannot occur (no command leaf supplied); treat it as admit
  // defensively so a future command leaf never silently drops a human turn.
  if (decision.kind === 'drop') return { admit: false, reason: decision.reason };
  return { admit: true };
}
